import { useAuthStore } from '../store/authStore'
import type { AlertDetail, AlertFilters, AlertsPage, GeoJSONFeatureCollection } from '../types/cap'
import { AU_STATES } from '../constants/auStates'

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

function authHeaders(): Record<string, string> {
  const stored = localStorage.getItem('cap-auth')
  if (stored) {
    try {
      const parsed = JSON.parse(stored) as { state?: { token?: string } }
      const tok = parsed.state?.token
      if (tok) return { Authorization: `Bearer ${tok}` }
    } catch {
      // ignore malformed persisted state
    }
  }
  return {}
}

function handleAuthFailure() {
  useAuthStore.getState().clearAuth()
}

async function get<T>(path: string, params?: Record<string, string | number | boolean>): Promise<T> {
  const url = new URL(BASE + path, window.location.origin)
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== '' && v !== null) url.searchParams.set(k, String(v))
    })
  }
  const res = await fetch(url.toString(), { headers: authHeaders() })
  if (res.status === 401) { handleAuthFailure(); throw new Error('401 Unauthorized') }
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
  return res.json() as Promise<T>
}

export interface FetchAlertsParams {
  filters: AlertFilters
  limit?: number
  offset?: number
}

export async function fetchAlerts({ filters, limit = 50, offset = 0 }: FetchAlertsParams): Promise<AlertsPage> {
  const params: Record<string, string | number | boolean> = { limit, offset }
  if (filters.active_only) params.active = true
  if (filters.hazard_types.length > 0) params.hazard_type = filters.hazard_types.join(',')
  if (filters.severity.length > 0) params.severity = filters.severity.join(',')
  if (filters.min_priority > 1) params.min_priority = filters.min_priority
  if (filters.region) {
    const bbox = AU_STATES.find((s) => s.code === filters.region)?.bbox
    if (bbox) params.bbox = bbox.join(',')
  }
  return get<AlertsPage>('/alerts', params)
}

export async function fetchAlert(id: string): Promise<AlertDetail> {
  return get<AlertDetail>(`/alerts/${id}`)
}

export async function fetchAlertGeoJSON(id: string): Promise<GeoJSONFeatureCollection> {
  return get<GeoJSONFeatureCollection>(`/alerts/${id}/geojson`)
}
